/**
 * Pure helpers for sidebar participant ordering and off-screen speaker promotion.
 *
 * These functions are intentionally side-effect-free so they can be unit-tested
 * without rendering the full LiveKit controller component.
 */

/**
 * Reconcile a stored sidebar order with a new base order.
 *
 * - Participants that left are dropped.
 * - Existing participants keep their current (potentially promoted) positions.
 * - New participants are appended at the end.
 * - Returns `prev` by reference when nothing changed (enables React bail-out).
 */
export function reconcileSidebarOrder(
  prev: string[],
  baseOrder: string[],
): string[] {
  if (prev.length === 0) return baseOrder;

  const baseSet = new Set(baseOrder);
  const kept = prev.filter(id => baseSet.has(id));

  const keptSet = new Set(kept);
  const newIds = baseOrder.filter(id => !keptSet.has(id));

  const result = [...kept, ...newIds];

  // Bail out if content is unchanged to avoid unnecessary re-renders
  if (result.length === prev.length && result.every((id, i) => prev[i] === id)) {
    return prev;
  }
  return result;
}

/**
 * Promote off-screen speakers into a visible sidebar position.
 *
 * For each speaking participant whose index >= viewportCapacity, splice them
 * into the first slot after the pinned items (screen sharers + local user).
 *
 * Returns `order` by reference when nothing changed.
 */
export function promoteSpeakersIntoViewport(
  order: string[],
  speakingIds: Set<string>,
  viewportCapacity: number,
  pinnedIds: Set<string>,
): string[] {
  let result = [...order];
  let changed = false;

  for (const id of speakingIds) {
    const idx = result.indexOf(id);
    if (idx === -1 || idx < viewportCapacity) continue;

    // Find insert position: after last consecutive pinned item
    let insertAt = 0;
    for (let i = 0; i < result.length && i < viewportCapacity; i++) {
      if (pinnedIds.has(result[i])) {
        insertAt = i + 1;
      } else {
        break;
      }
    }

    result.splice(idx, 1);
    result.splice(insertAt, 0, id);
    changed = true;
  }

  return changed ? result : order;
}

/**
 * Pinned and promoted ids for the sidebar and gallery. Screen sharers and raised
 * hands behave like speakers (pulled into view when off-screen) and are also
 * pinned, so once visible a new speaker is placed after them instead of pushing
 * them back out. Sharers have to be promoted, not just sorted first: someone
 * already listed keeps their slot, and in the gallery a sharer's tile is the only
 * way to open their share (SC-375).
 */
export function sidebarPromotionSets(
  participants: ReadonlyArray<{ identity: string; isLocal?: boolean }>,
  screenShareParticipantIds: ReadonlySet<string>,
  speakingIds: ReadonlySet<string>,
  handRaisedIds: ReadonlySet<string> = new Set(),
): { pinnedIds: Set<string>; promoteIds: Set<string> } {
  const pinnedIds = new Set<string>();
  const promoteIds = new Set<string>(speakingIds);
  for (const p of participants) {
    if (p.isLocal) pinnedIds.add(p.identity);
    if (screenShareParticipantIds.has(p.identity) || handRaisedIds.has(p.identity)) {
      pinnedIds.add(p.identity);
      promoteIds.add(p.identity);
    }
  }
  return { pinnedIds, promoteIds };
}

/**
 * Comparator for the sidebar's base order: screen sharers, then you, then
 * raised hands, then unmuted before muted. Everyone else keeps their relative
 * order (`Array.prototype.sort` is stable).
 */
export function compareSidebarParticipants(
  screenShareParticipantIds: ReadonlySet<string>,
  handRaisedIds: ReadonlySet<string>,
): (
  a: { identity: string; isLocal?: boolean; isMicrophoneEnabled?: boolean },
  b: { identity: string; isLocal?: boolean; isMicrophoneEnabled?: boolean },
) => number {
  return (a, b) => {
    const aSharing = screenShareParticipantIds.has(a.identity);
    const bSharing = screenShareParticipantIds.has(b.identity);
    if (aSharing !== bSharing) return aSharing ? -1 : 1;
    if (!!a.isLocal !== !!b.isLocal) return a.isLocal ? -1 : 1;
    const aHand = handRaisedIds.has(a.identity);
    const bHand = handRaisedIds.has(b.identity);
    if (aHand !== bHand) return aHand ? -1 : 1;
    const aMuted = !a.isMicrophoneEnabled;
    const bMuted = !b.isMicrophoneEnabled;
    if (aMuted !== bMuted) return aMuted ? 1 : -1;
    return 0;
  };
}
